import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Big, Delta, fmtMonth } from "./report/charts";
import { Question, ORDER } from "./report/Question";
import { Conclusions } from "./report/Conclusions";
import { fmtEur, fmtPct, fmtDate } from "./labels";

export const PERIODS = [{ key: "2026", label: "2026" }, { key: "90d", label: "Últimos 90 días" }, { key: "month", label: "Último mes" }, { key: "custom", label: "Otro…" }];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;  // local date, not UTC
export function periodRange(key) {
  const now = new Date();
  if (key === "90d") return { date_from: iso(new Date(now - 90 * 864e5)), date_to: iso(now) };
  if (key === "month") { const f = new Date(now.getFullYear(), now.getMonth() - 1, 1); const t = new Date(now.getFullYear(), now.getMonth(), 0); return { date_from: iso(f), date_to: iso(t) }; }
  return { date_from: "2026-01-01", date_to: iso(now) };
}

function TagProgress({ job }) {
  if (!job || job.status !== "running") return null;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="border border-clay-200 bg-white px-4 py-3 text-xs" data-testid="kk-r-tagging">
      <div className="flex justify-between mb-1.5"><span>Etiquetando con IA los leads cerrados · {job.done} de {job.total}</span><span className="tabular">{pct} %</span></div>
      <div className="h-1.5 bg-clay-100"><div className="h-full bg-terracotta transition-[width]" style={{ width: `${pct}%` }}/></div>
      <div className="text-clay-500 mt-1.5">Puedes leer el informe mientras tanto; la pregunta 3 se completa al terminar.</div>
    </div>
  );
}

function Header({ h, period }) {
  const lm = h.last_month, pm = h.prev_month;
  const vs = lm && pm ? `${fmtMonth(lm.month)} frente a ${fmtMonth(pm.month)}` : null;
  return (
    <section className="bg-white border border-clay-200 px-6 py-6 md:px-8" data-testid="kk-r-header">
      <div className="text-[11px] text-clay-500 mb-4" data-testid="kk-r-scope">
        Del {fmtDate(period.from)} al {fmtDate(period.to)} · {h.leads} leads solicitados · {h.closed} cerrados · conversión <strong>{fmtPct(h.conversion)}</strong>
        {vs && <> · variación: {vs}</>}
      </div>
      <div className="flex flex-wrap gap-x-10 gap-y-5">
        <Big testid="kk-r-big-sales" label="Ventas" value={h.sales_count} sub={lm ? `${lm.sold} en ${fmtMonth(lm.month)}` : null} delta={<Delta pct={h.var_sales_pct} testid="kk-r-var-sales"/>}/>
        <Big testid="kk-r-big-eur" label="Venta total" value={fmtEur(h.sales_eur)} sub={lm ? fmtEur(lm.sales_eur) : null} delta={<Delta pct={h.var_sales_eur_pct}/>}/>
        <Big testid="kk-r-big-net" label="Ingreso neto" value={fmtEur(h.net_eur)} sub="sin comisión KimKim ni tasas"/>
        <Big testid="kk-r-big-margin" label="Margen" value={fmtEur(h.margin_eur)} sub={`${fmtPct(h.margin_pct)} · ${fmtEur(h.avg_margin)} por venta`} delta={<Delta pct={h.var_margin_pct} testid="kk-r-var-margin"/>}/>
      </div>
      {h.no_breakdown > 0 && <div className="text-[11px] text-clay-500 mt-3">{h.no_breakdown} venta(s) sin desglose: convertidas desde dólares, sin margen declarado.</div>}
    </section>
  );
}

export function Report({ queryParams, drill }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/kimkim/report", { params: queryParams });
      setReport(data);
    }
    catch (e) { const d = e?.response?.data?.detail; setError(typeof d === "string" ? d : "No se pudo calcular el informe"); }
    finally { setLoading(false); }
  }, [queryParams]);
  useEffect(() => { load(); }, [load]);
  const running = report?.ai_job?.status === "running";
  useEffect(() => { if (!running) return; const t = setInterval(load, 30000); return () => clearInterval(t); }, [running, load]);
  const aiParams = useMemo(() => ({ date_from: queryParams.date_from, date_to: queryParams.date_to, agent: queryParams.agent }), [queryParams]);

  if (error && !report) return <div className="text-sm text-terracotta py-8" data-testid="kk-r-error">{error} <button onClick={load} className="underline ml-2">Reintentar</button></div>;
  if (!report) return <div className="text-sm text-clay-600 py-8" data-testid="kk-r-loading">Calculando el informe…</div>;
  return (
    <div className={`space-y-6 ${loading ? "opacity-60" : ""}`} data-testid="kk-report">
      <TagProgress job={report.ai_job}/>
      <Header h={report.header} period={report.period}/>
      {ORDER.map((k, i) => <Question key={k} k={k} idx={i + 1} q={report.questions[k]} detail={report.detail} drill={drill} onTagged={load}/>)}
      <Conclusions queryParams={aiParams}/>
    </div>
  );
}
